// Inventaire en grille : chaque objet occupe un rectangle de cases. Placement automatique (première place libre).
import { ITEMS } from './items';

export interface ItemInst {
  uid: number;
  id: string;
  n: number;
  ammo?: number; // munitions dans le chargeur d'une arme
  free?: boolean; // kit civil : sans valeur à la revente
  dur?: number; // armure : durabilité restante
  max?: number; // armure : durabilité maximale actuelle (baisse à chaque réparation ; absente = neuve)
  uses?: number; // clé : utilisations restantes (absente = neuve)
  mods?: Partial<Record<'optic' | 'muzzle' | 'mag' | 'grip', string>>; // arme : accessoires montés (identifiants d'objets)
  ammoId?: string; // arme : type de cartouches dans le chargeur
  grid?: Grid; // sac à dos rangé : son contenu
}

export interface Placed extends ItemInst { x: number; y: number }

export interface Grid { w: number; h: number; items: Placed[] }

// Durabilité maximale d'une protection : celle d'origine, moins ce que les réparations lui ont fait perdre
export const maxDur = (it: ItemInst) => it.max ?? ITEMS[it.id]?.armor?.dur ?? 0;

export const newGrid = (w: number, h: number): Grid => ({ w, h, items: [] });

let uidSeq = Math.floor(Math.random() * 1e6) * 1000;
export const nextUid = () => ++uidSeq;
export function setUidFloor(v: number) { if (v > uidSeq) uidSeq = v; }

export function fits(g: Grid, x: number, y: number, w: number, h: number, ignore?: number): boolean {
  if (x < 0 || y < 0 || x + w > g.w || y + h > g.h) return false;
  for (const it of g.items) {
    if (it.uid === ignore) continue;
    const d = ITEMS[it.id]!;
    if (x < it.x + d.w && x + w > it.x && y < it.y + d.h && y + h > it.y) return false;
  }
  return true;
}

export function findSpot(g: Grid, id: string): [number, number] | null {
  const d = ITEMS[id]!;
  for (let y = 0; y + d.h <= g.h; y++) for (let x = 0; x + d.w <= g.w; x++) if (fits(g, x, y, d.w, d.h)) return [x, y];
  return null;
}

// Ajoute un objet : complète d'abord les piles existantes, puis cherche de la place. Retourne la quantité NON rangée.
export function addItem(g: Grid, item: ItemInst): number {
  const d = ITEMS[item.id];
  if (!d) return item.n;
  let left = item.n;
  if (d.stack > 1) {
    for (const it of g.items) {
      if (it.id !== item.id || it.n >= d.stack || !!it.free !== !!item.free) continue;
      const k = Math.min(left, d.stack - it.n);
      it.n += k;
      left -= k;
      if (!left) return 0;
    }
  }
  while (left > 0) {
    const spot = findSpot(g, item.id);
    if (!spot) return left;
    const k = Math.min(left, d.stack);
    g.items.push({ ...item, uid: left === item.n ? item.uid : nextUid(), n: k, x: spot[0], y: spot[1] });
    left -= k;
  }
  return 0;
}

// Rangement automatique : regroupe les piles incomplètes, puis replace tout par catégorie (armes, protections, sacs,
// munitions, soins, butin), les plus gros objets d'abord. Si tout ne rentre pas, la grille reste telle quelle.
const CAT_ORDER = ['weapon', 'armor', 'bag', 'ammo', 'med', 'loot'];
export function sortGrid(g: Grid): boolean {
  const merged: ItemInst[] = [];
  const piles = new Map<string, number>();
  for (const it of g.items) {
    const d = ITEMS[it.id]!;
    if (d.stack > 1 && !it.grid) {
      const k = `${it.id}|${it.free ? 1 : 0}`;
      piles.set(k, (piles.get(k) ?? 0) + it.n);
      if (!merged.some(m => `${m.id}|${m.free ? 1 : 0}` === k && m.n === -1)) merged.push({ ...it, n: -1 });
    } else merged.push({ ...it });
  }
  const list: ItemInst[] = [];
  for (const it of merged) {
    if (it.n !== -1) { list.push(it); continue; }
    const d = ITEMS[it.id]!;
    let left = piles.get(`${it.id}|${it.free ? 1 : 0}`)!, first = true;
    while (left > 0) { const k = Math.min(left, d.stack); list.push({ ...it, uid: first ? it.uid : nextUid(), n: k }); left -= k; first = false; }
  }
  const key = (it: ItemInst) => { const d = ITEMS[it.id]!; return [CAT_ORDER.indexOf(d.cat), -d.w * d.h, -d.h, it.id, -it.n] as const; };
  list.sort((a, b) => { const ka = key(a), kb = key(b); for (let i = 0; i < ka.length; i++) if (ka[i] !== kb[i]) return ka[i]! < kb[i]! ? -1 : 1; return 0; });
  const out: Grid = { w: g.w, h: g.h, items: [] };
  for (const it of list) {
    const spot = findSpot(out, it.id);
    if (!spot) return false;
    const { x: _x, y: _y, ...rest } = it as Placed;
    out.items.push({ ...rest, x: spot[0], y: spot[1] });
  }
  g.items = out.items;
  return true;
}

export function removeUid(g: Grid, uid: number): Placed | null {
  const i = g.items.findIndex(it => it.uid === uid);
  return i < 0 ? null : g.items.splice(i, 1)[0]!;
}

export function countOf(g: Grid, id: string) { return g.items.reduce((s, it) => s + (it.id === id ? it.n : 0), 0); }

// Retire jusqu'à n unités d'un objet empilable ; retourne la quantité retirée
export function takeCount(g: Grid, id: string, n: number): number {
  let got = 0;
  for (const it of [...g.items].sort((a, b) => a.n - b.n)) {
    if (it.id !== id || got >= n) continue;
    const k = Math.min(it.n, n - got);
    it.n -= k;
    got += k;
    if (!it.n) removeUid(g, it.uid);
  }
  return got;
}

// Valeur d'une grille, contenu des sacs à dos compris
// Valeur d'un objet : lui-même, ses accessoires montés et, pour un sac, son contenu
export const modsValue = (it: ItemInst) => Object.values(it.mods ?? {}).reduce((s, m) => s + (m ? ITEMS[m]?.value ?? 0 : 0), 0);
export const gridValue = (g: Grid): number => g.items.reduce((s, it) => s + (ITEMS[it.id]?.value ?? 0) * it.n + modsValue(it) + (it.grid ? gridValue(it.grid) : 0), 0);

// Pose un objet à une case précise. Sur une pile du même objet, on la complète. Retourne la quantité NON posée.
export function placeAt(g: Grid, item: ItemInst, x: number, y: number): number {
  const d = ITEMS[item.id];
  if (!d) return item.n;
  if (fits(g, x, y, d.w, d.h)) {
    const k = Math.min(item.n, d.stack);
    g.items.push({ ...item, n: k, x, y });
    return item.n - k;
  }
  const onto = g.items.find(i => i.id === item.id && !!i.free === !!item.free && x >= i.x && x < i.x + d.w && y >= i.y && y < i.y + d.h);
  if (!onto || onto.n >= d.stack) return item.n;
  const k = Math.min(item.n, d.stack - onto.n);
  onto.n += k;
  return item.n - k;
}
